/**
 * GoPay MoR §13 — (a) unrestricted vs (b) pickup-only shipping disclosure.
 */
import { useTranslation } from 'react-i18next';
import { isShippingModalityUxVisible, useEligibleShippingMethods } from '../checkout/shippingCheckoutApi.js';
import {
  resolveShippingDisclosure,
  type ShippingDisclosureResult,
} from './shippingDisclosureModel.js';

export interface ShippingDisclosureBannerProps {
  readonly salesPointId: number | null;
  readonly testIdPrefix?: string;
}

function disclosureCopy(
  result: ShippingDisclosureResult,
  t: (key: string, opts?: Record<string, string>) => string,
): string {
  if (result.kind === 'pickup_only') {
    return t('morChrome.shipping.pickupOnly');
  }
  if (result.kind === 'allowlist') {
    return t('morChrome.shipping.allowlist', {
      countries: result.countryCodes.join(', '),
    });
  }
  return t('morChrome.shipping.unrestricted');
}

export function ShippingDisclosureBanner({
  salesPointId,
  testIdPrefix = 'customer-shipping-disclosure',
}: ShippingDisclosureBannerProps): JSX.Element | null {
  const { t } = useTranslation('customer');
  const eligible = useEligibleShippingMethods(salesPointId);
  const data = eligible.data;

  // Until eligible settles, show pickup-only honesty only when query succeeded with no policy.
  if (eligible.isPending || eligible.isError) {
    return null;
  }

  const hasActive =
    isShippingModalityUxVisible(data) &&
    data?.policy != null &&
    data.policy.active !== false;

  const result = resolveShippingDisclosure({
    hasActiveDeliveryPolicy: hasActive,
    allowedCountryCodes: data?.policy?.allowedCountryCodes ?? [],
  });

  return (
    <div
      className="rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm text-[var(--color-on-surface)]"
      data-testid={testIdPrefix}
      data-disclosure-kind={result.kind}
      role="status"
    >
      {disclosureCopy(result, t)}
    </div>
  );
}
